"use client";

import React from "react";
import { LocaleLink as Link } from "@/components/shared/LocaleLink";
import { useTranslations } from "@/lib/i18n-client";
import { Reveal, Stagger, StaggerItem } from "@/components/shared/motion";
import { Wifi, ArrowRight } from "lucide-react";

type Destination = { code: string; name: string; price?: string };

/**
 * AquaGlobe popular destinations — frosted-glass landmark cards. Each card:
 * flag (flagcdn) + soft gradient header, country name, 4G/5G coverage with a
 * live network-status dot, from-price, and a teal "View plans" pill routing to
 * /destinations/<code>/. Glass-native; presentational, data wired by the route.
 */
export default function PopularDestinations({ destinations }: { destinations: { code: string; name: string; price?: string }[] }) {
  const { t } = useTranslations();

  return (
    <section className="container mx-auto px-4 py-12">
      <Reveal as="div" className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6" y={16}>
        <h2 className="text-2xl sm:text-3xl font-bold text-foreground">
          {t("aqua.destinations.title", "Popular Destinations")}
        </h2>
        <Link
          href="/destinations/"
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline"
        >
          {t("aqua.destinations.viewAll", "View all destinations")} <ArrowRight className="h-4 w-4" />
        </Link>
      </Reveal>

      <Stagger
        className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5"
        staggerChildren={0.06}
        amount={0.15}
      >
        {destinations.map((destination: Destination) => {
          const code = destination.code.toLowerCase();
          return (
            <StaggerItem
              as="div"
              y={14}
              key={destination.code}
              className="aqua-glass group rounded-3xl p-5 backdrop-blur hover:-translate-y-1 transition-transform duration-300"
            >
              <div className="aqua-gradient-soft relative h-24 rounded-2xl flex items-center justify-center mb-4 overflow-hidden">
                <img
                  src={`https://flagcdn.com/${code}.svg`}
                  alt=""
                  role="presentation"
                  width={56}
                  height={40}
                  className="h-10 w-14 rounded shadow"
                  loading="lazy"
                  referrerPolicy="no-referrer"
                />
              </div>

              <div className="font-semibold text-foreground">{destination.name}</div>
              <div className="mt-1 inline-flex items-center gap-2 text-xs text-muted-foreground">
                <span className="aqua-dot" aria-hidden="true" />
                <Wifi className="h-3.5 w-3.5 text-primary" />
                {t("aqua.destinations.coverage", "4G/5G coverage")}
              </div>

              {destination.price && (
                <div className="mt-3 text-sm text-foreground">
                  <span className="text-muted-foreground">{t("aqua.destinations.from", "from")} </span>
                  <span className="font-bold">{destination.price}</span>
                </div>
              )}

              <Link
                href={`/destinations/${code}/`}
                className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-full bg-primary text-primary-foreground font-semibold h-11 shadow-lg shadow-primary/20 hover:bg-primary/90 transition-all active:scale-[0.98]"
              >
                {t("aqua.destinations.view", "View plans")} <ArrowRight className="h-4 w-4" />
              </Link>
            </StaggerItem>
          );
        })}
      </Stagger>
    </section>
  );
}
